import React from 'react'
import { FormProvider } from 'react-hook-form'
import { Platform, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import Button from '@ui/buttons/Button'
import ModalWide from '@ui/components/Modal/variants/ModalWide'
import { DevFillSectionButton } from '../DevFillSectionButton'
import type { CofidisSectionConfig } from '../sections'
import type { CofidisSectionFieldSuccess } from '../types'
import type { CofidisApplicationForm } from '../useCofidisApplicationForm'
import Typography from '@ui/typography/Typography'

export type SectionModalProps = {
  open: boolean
  close: () => void
  section: CofidisSectionConfig
  form: CofidisApplicationForm
  fieldSuccess?: CofidisSectionFieldSuccess
  showNextButton: boolean
  onNext: () => void
  onPrev?: () => void
  beforeClose: () => boolean
  onSubmitSection: () => void
  progressComponent?: React.ReactNode
}

export default function SectionModal({
  open,
  close,
  section,
  form,
  fieldSuccess,
  showNextButton,
  onNext,
  onPrev,
  onSubmitSection,
  beforeClose,
  progressComponent,
}: SectionModalProps) {
  const { t } = useTranslation(['ui'])
  const text = `cofidis:sections.${section.name}`
  const SectionComponent = section.Component

  return (
    <ModalWide
      hideBottomSpace
      center
      cardComponentProps={{
        justifyContent: Platform.OS === 'web' ? 'flex-end' : 'flex-end',
      }}
      modalProps={{ beforeClose }}
      open={open}
      close={close}
      showClose
    >
      {/* We need to add an extra provider since this is in a portal  */}
      <FormProvider {...form}>
        <View className="flex flex-col">
          <View className="relative inset-0 flex h-24">
            {progressComponent}
            <View className="absolute left-8 top-8 flex flex-row items-center justify-start gap-4 rounded-full bg-white">
              <Button
                variant="outlined"
                size="sm"
                iconStart="chevron-left"
                onPress={onPrev}
              />
              <Button
                variant="outlined"
                size="sm"
                iconEnd="chevron-right"
                onPress={onNext}
              />
            </View>
          </View>
          <View className="flex flex-col gap-2 px-8 pb-16">
            <Typography variant="XLB" className="text-center">
              {t(`${text}.header.title`)}
            </Typography>
            <Typography variant="MD" className="text-center color-desc">
              {t(`${text}.header.desc`)}
            </Typography>
          </View>
          <View className=" flex flex-col px-8">
            <SectionComponent fieldSuccess={fieldSuccess} />
            <DevFillSectionButton section={section} className="right-9 top-1" />
          </View>

          <View className="flex-row items-center justify-center gap-3 px-8 py-16">
            {showNextButton ? (
              <Button
                variant="filled"
                label={t('ui:next')}
                iconEnd="arrow-right"
                onPress={onNext}
              />
            ) : (
              <Button
                variant="outlined"
                label={t('ui:confirm')}
                iconEnd="arrow-right"
                onPress={onSubmitSection}
              />
            )}
          </View>
        </View>
      </FormProvider>
    </ModalWide>
  )
}
